import { AxiosRequestConfig, AxiosResponse } from 'axios'

import { Api } from 'common/api/api'
import { apiConfig } from 'common/api/api.config'
import {
  AiAvailability,
  AiTranslationsSettingsResponse,
  AiSettingsResponse,
  AiSettingsScope,
  UpdateAiAvailabilityPayload,
  UpdateAiTranslationsSettingsPayload,
  UpdateAiSettingsPayload
} from 'common/types/aiSettings'
import { AiCostReport, AiCostReportParams } from 'common/types/aiCosts'
import { GenerateDocumentationArgs, GenerateDocumentationResponse } from 'common/types/documenter'
import { Agent, SessionSummary } from 'store/agent'
import { UserView } from 'store/socket/agentLiveView'

export interface UserViewReportResponse {
  agentAttached: boolean
  expiresIn: number
  clientName?: string | null
}

export interface UserViewCaptureUpload {
  requestId: string
  status: 'ready' | 'not_visible' | 'failed'
  image?: Blob
  details?: Record<string, unknown>
}

const ENDPOINT = '/ai'

export interface GeneratedFileUrl {
  /** Path relative to the API root; single-use and short-lived. */
  url: string
  expiresAt: string
  name: string
  sizeBytes: number
  contentType: string
}

class AIApi extends Api {
  public constructor(config: AxiosRequestConfig) {
    super(config)

    // this middleware is been called right before the http request is made.
    this.interceptors.request.use((param: AxiosRequestConfig) => ({
      ...param
    }))

    // this middleware is been called right before the response is get it by the method that triggers the request
    this.interceptors.response.use((param: AxiosResponse) => ({
      ...param
    }))

    Object.setPrototypeOf(this, AIApi.prototype)
    this.getAgents = this.getAgents.bind(this)
    this.chat = this.chat.bind(this)
    this.generateDocumentation = this.generateDocumentation.bind(this)
    this.stopAgent = this.stopAgent.bind(this)
    this.speechToText = this.speechToText.bind(this)
    this.getSessions = this.getSessions.bind(this)
    this.getSessionMessages = this.getSessionMessages.bind(this)
    this.getDownloadUrl = this.getDownloadUrl.bind(this)
    this.deleteSession = this.deleteSession.bind(this)
    this.restoreCheckpoint = this.restoreCheckpoint.bind(this)
    this.renameSession = this.renameSession.bind(this)
    this.getSettings = this.getSettings.bind(this)
    this.updateSettings = this.updateSettings.bind(this)
    this.getAvailability = this.getAvailability.bind(this)
    this.updateAvailability = this.updateAvailability.bind(this)
    this.getCostReport = this.getCostReport.bind(this)
    this.getTranslationSettings = this.getTranslationSettings.bind(this)
    this.updateTranslationSettings = this.updateTranslationSettings.bind(this)
  }

  /**
   * Monthly AI cost per company, split into what is billable to the client and
   * what allowlisted (internal) email domains consumed. Requires
   * `manage_ai_pricing`; scoped to the companies assigned to the user.
   */
  public async getCostReport(params: AiCostReportParams = {}): Promise<AiCostReport> {
    try {
      const res: AxiosResponse<AiCostReport> = await this.get(`${ENDPOINT}/costs/getReport/`, {
        params
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /**
   * Whether AI features are usable right now for the current company. Readable
   * by any authenticated user, unlike the `/ai/settings/` endpoints.
   */
  public async getAvailability(): Promise<AiAvailability> {
    try {
      const res: AxiosResponse<AiAvailability> = await this.get(`${ENDPOINT}/getAiAvailability/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /**
   * Turn AI on/off for a scope and set its monthly token limit. Patches only
   * those keys, leaving the agent/model configuration in the same JSON intact.
   */
  public async updateAvailability(
    payload: UpdateAiAvailabilityPayload
  ): Promise<AiSettingsResponse> {
    try {
      const res: AxiosResponse<AiSettingsResponse> = await this.post(
        `${ENDPOINT}/settings/updateAvailability/`,
        payload
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getAgents(): Promise<Agent[]> {
    try {
      const res: AxiosResponse<Agent[]> = await this.get(`${ENDPOINT}/getAgents/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getSettings(scope: AiSettingsScope): Promise<AiSettingsResponse> {
    try {
      const res: AxiosResponse<AiSettingsResponse> = await this.get(
        `${ENDPOINT}/settings/getSettings/`,
        {
          params: { scope }
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async updateSettings(payload: UpdateAiSettingsPayload): Promise<AiSettingsResponse> {
    try {
      const res: AxiosResponse<AiSettingsResponse> = await this.post(
        `${ENDPOINT}/settings/updateSettings/`,
        payload
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getTranslationSettings(): Promise<AiTranslationsSettingsResponse> {
    try {
      const res: AxiosResponse<AiTranslationsSettingsResponse> = await this.get(
        `${ENDPOINT}/settings/getTranslationSettings/`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async updateTranslationSettings(
    payload: UpdateAiTranslationsSettingsPayload
  ): Promise<AiTranslationsSettingsResponse> {
    try {
      const res: AxiosResponse<AiTranslationsSettingsResponse> = await this.post(
        `${ENDPOINT}/settings/updateTranslationSettings/`,
        payload
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async reportUserView(view: UserView): Promise<UserViewReportResponse> {
    try {
      const res: AxiosResponse<UserViewReportResponse> = await this.post(
        `${ENDPOINT}/userView/`,
        view
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async chat(
    agentCode: string,
    chatMessage: { role: string; content?: string },
    chatSessionId?: string,
    metadata?: Record<string, any>,
    context?: Record<string, any>,
    stream: boolean = true
  ): Promise<AxiosResponse<any>> {
    try {
      const res: AxiosResponse<any> = await this.post(`${ENDPOINT}/chat/`, {
        agent_code: agentCode,
        chat_message: chatMessage,
        chat_session_id: chatSessionId,
        metadata,
        context,
        stream
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async generateDocumentation(
    args: GenerateDocumentationArgs
  ): Promise<GenerateDocumentationResponse> {
    try {
      const body: Record<string, any> = {
        agent_code: args.agent_code,
        metadata: args.metadata,
        context: args.context
      }
      if ('payload' in args && args.payload !== undefined) body.payload = args.payload
      const res: AxiosResponse<GenerateDocumentationResponse> = await this.post(
        `${ENDPOINT}/generateDocumentation/`,
        body
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async stopAgent(agentCode: string, chatSessionId: string): Promise<AxiosResponse<any>> {
    try {
      const res: AxiosResponse<any> = await this.post(`${ENDPOINT}/stop/`, {
        agentCode,
        chatSessionId
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getSessions(agentCode: string): Promise<SessionSummary[]> {
    try {
      const res: AxiosResponse<SessionSummary[]> = await this.get(`${ENDPOINT}/getSessions/`, {
        params: { agent_code: agentCode }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getSessionMessages(
    agentCode: string,
    chatSessionId: string
  ): Promise<{
    messages: { role: string; content: string }[]
    context_usage: { used_tokens: number; budget_tokens: number; percent: number } | null
  }> {
    try {
      const res: AxiosResponse<{
        messages: { role: string; content: string }[]
        context_usage: { used_tokens: number; budget_tokens: number; percent: number } | null
      }> = await this.post(`${ENDPOINT}/getSessionMessages/`, { agentCode, chatSessionId })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /**
   * Single-use URL for a file the agent generated. Minted on click — the chat
   * only ever stores the file name, so a stored link can never go stale.
   */
  public async getDownloadUrl(fileName: string): Promise<GeneratedFileUrl> {
    try {
      const res: AxiosResponse<GeneratedFileUrl> = await this.post(`${ENDPOINT}/getDownloadUrl/`, {
        fileName
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async deleteSession(
    agentCode: string,
    chatSessionId: string
  ): Promise<{ deleted: boolean }> {
    try {
      const res: AxiosResponse<{ deleted: boolean }> = await this.post(
        `${ENDPOINT}/deleteSession/`,
        { agentCode, chatSessionId }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async restoreCheckpoint(
    agentCode: string,
    chatSessionId: string,
    checkpointIndex: number,
    interfaceId: string = ''
  ): Promise<{ role: string; content: string }[]> {
    try {
      const res: AxiosResponse<{ role: string; content: string }[]> = await this.post(
        `${ENDPOINT}/restoreCheckpoint/`,
        {
          agentCode,
          chatSessionId,
          checkpointIndex,
          restoreInterfaceChanges: !!interfaceId,
          interfaceId
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async renameSession(
    agentCode: string,
    chatSessionId: string,
    title: string
  ): Promise<{ renamed: boolean }> {
    try {
      const res: AxiosResponse<{ renamed: boolean }> = await this.post(
        `${ENDPOINT}/renameSession/`,
        { agentCode, chatSessionId, title }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getInterfaceHistoryState(
    agentCode: string,
    chatSessionId: string,
    checkpointIndex: number,
    interfaceId: string
  ): Promise<boolean> {
    try {
      const res: AxiosResponse<{
        hasSnapshot: boolean
      }> = await this.get(`${ENDPOINT}/getInterfaceHistoryState/`, {
        params: { agentCode, chatSessionId, checkpointIndex, interfaceId }
      })
      return this.success(res)?.hasSnapshot ?? false
    } catch (error) {
      return false
    }
  }

  public async uploadUserViewCapture(capture: UserViewCaptureUpload): Promise<void> {
    const formData = new FormData()
    formData.append('requestId', capture.requestId)
    formData.append('status', capture.status)
    formData.append('details', JSON.stringify(capture.details ?? {}))
    if (capture.image) formData.append('image', capture.image, 'capture.png')
    const res: AxiosResponse<void> = await this.post(`${ENDPOINT}/userViewCapture/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return this.success(res)
  }

  public async speechToText(audioFile: File): Promise<string> {
    try {
      const formData = new FormData()
      formData.append('audio_file', audioFile)

      const res: AxiosResponse<string> = await this.post(`${ENDPOINT}/speechToText/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
}

export const aiApi = new AIApi(apiConfig)
